import type { AnyValue } from "@/lib/rowTypes";
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useCareerActivation } from "./useCareerActivation";

/**
 * Categories / keys that should ONLY surface once the learner has actually
 * activated a career or business path. A learner who hasn't entered a
 * job/business yet must not receive paycheck / workplace / revenue / startup
 * style life events.
 */
const CAREER_BUSINESS_PATTERN =
  /career|job|paycheck|payroll|salary|wage|work(place|er|_)?|coworker|boss|manager|promotion|raise|business|startup|revenue|customer|client|employee|vendor|invoice|sales|profit|loss|entrepreneur|side[_ ]?hustle/i;

function isCareerOrBusinessTemplate(tpl: LifeEventTemplate): boolean {
  const haystack = [
    tpl.event_category,
    tpl.event_key,
    tpl.event_name,
    tpl.event_description,
  ]
    .filter(Boolean)
    .join(" ");
  return CAREER_BUSINESS_PATTERN.test(haystack);
}

/* ─── Types ─── */

export interface EventChoice {
  label: string;
  account_type: string;
  amount: number;
  is_deduction: boolean;
  stress_delta: number;
  resilience_delta: number;
  confidence_delta: number;
  quality: "excellent" | "good" | "fair" | "poor";
  explanation: string;
}

export interface LifeEventTemplate {
  id: string;
  event_key: string;
  event_name: string;
  event_category: string;
  event_difficulty: string;
  event_description: string;
  event_description_11_13: string | null;
  event_description_14_16: string | null;
  event_description_17_plus: string | null;
  event_choices: EventChoice[];
  event_followup_text: string | null;
  event_trigger_type: string;
  event_trigger_course_id: string | null;
  event_trigger_module_id: string | null;
  event_frequency_weight: number;
}

export interface ActiveLifeEvent {
  template: LifeEventTemplate;
  lessonId: string;
  resolvedDescription: string;
}

export interface LearnerOutcomes {
  stress_score: number;
  resilience_score: number;
  financial_confidence_score: number;
  total_events_faced: number;
  total_events_handled_well: number;
}

/* ─── Difficulty mapping ─── */

function getLearnerDifficulty(lessonsCompleted: number): string {
  if (lessonsCompleted < 10) return "beginner";
  if (lessonsCompleted < 25) return "intermediate";
  return "advanced";
}

/* ─── Hook ─── */

export function useLifeEvents() {
  const { user } = useAuth();
  const { activated: careerActivated } = useCareerActivation();
  const [activeEvent, setActiveEvent] = useState<ActiveLifeEvent | null>(null);
  const [outcomes, setOutcomes] = useState<LearnerOutcomes | null>(null);
  const [templates, setTemplates] = useState<LifeEventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const lessonsSinceEvent = useRef(0);

  // Fetch templates & outcomes
  const fetchData = useCallback(async () => {
    if (!user) { setLoading(false); return; }

    const [{ data: tpls }, { data: outc }] = await Promise.all([
      supabase.from("life_event_templates").select("*").eq("is_active", true),
      supabase.from("learner_outcomes").select("*").eq("user_id", user.id).maybeSingle(),
    ]);

    setTemplates((tpls as AnyValue) || []);
    if (outc) {
      setOutcomes(outc);
    } else {
      await supabase.rpc("ensure_learner_outcomes");
      setOutcomes({ stress_score: 50, resilience_score: 50, financial_confidence_score: 50, total_events_faced: 0, total_events_handled_well: 0 });
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Init lesson counter
  useEffect(() => {
    if (!user) return;
    const init = async () => {
      const { data: lastEvent } = await supabase
        .from("user_event_log")
        .select("created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (lastEvent?.length) {
        const { count } = await supabase
          .from("account_transactions")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("transaction_type", "completion_bonus")
          .gt("created_at", (lastEvent[0]).created_at);
        lessonsSinceEvent.current = count || 0;
      } else {
        const { count } = await supabase
          .from("user_progress")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("completed", true);
        lessonsSinceEvent.current = count || 0;
      }
    };
    init();
  }, [user]);

  // Use the default description (mastery-tier swap removed in Phase 2C).
  const resolveDescription = useCallback((tpl: LifeEventTemplate): string => {
    return tpl.event_description_14_16 || tpl.event_description;
  }, []);

  // Should trigger
  const shouldTriggerEvent = useCallback((): boolean => {
    lessonsSinceEvent.current += 1;
    const since = lessonsSinceEvent.current;
    if (since < 3) return false;
    if (since >= 5) { lessonsSinceEvent.current = 0; return true; }
    const chance = since === 3 ? 0.3 : 0.5;
    if (Math.random() < chance) { lessonsSinceEvent.current = 0; return true; }
    return false;
  }, []);

  // Pick weighted random template
  const pickTemplate = useCallback((lessonsCompleted: number): LifeEventTemplate | null => {
    if (!templates.length) return null;
    const difficulty = getLearnerDifficulty(lessonsCompleted);
    // Eligibility gate: filter out career/business templates if the learner
    // hasn't unlocked the appropriate path yet. This prevents ineligible
    // paycheck / workplace / business events from surfacing early.
    const eligible = careerActivated
      ? templates
      : templates.filter((t) => {
          const blocked = isCareerOrBusinessTemplate(t);
          if (blocked) {
            console.log("Life event eligibility evaluated", {
              eventKey: t.event_key,
              eventCategory: t.event_category,
              requiredUnlock: "career_or_business",
              hasCareerPath: careerActivated,
              eligible: false,
              blockedReason: "career/business event requires activated career path",
            });
          }
          return !blocked;
        });
    if (!eligible.length) return null;
    // Prefer matching difficulty, but allow others
    const matching = eligible.filter(t => t.event_difficulty === difficulty);
    const pool = matching.length >= 2 ? matching : eligible;
    // Weighted random
    const totalWeight = pool.reduce((s, t) => s + (t.event_frequency_weight || 50), 0);
    let r = Math.random() * totalWeight;
    for (const t of pool) {
      r -= (t.event_frequency_weight || 50);
      if (r <= 0) return t;
    }
    return pool[pool.length - 1];
  }, [templates, careerActivated]);

  // Check and trigger
  const checkAndTriggerEvent = useCallback((lessonId: string, lessonsCompleted: number): boolean => {
    // Life events always fire regardless of Career Path activation.
    // Job/career/side-hustle events are gated separately in useEventEngine.
    if (!shouldTriggerEvent()) return false;
    const tpl = pickTemplate(lessonsCompleted);
    if (!tpl) return false;
    setActiveEvent({
      template: tpl,
      lessonId,
      resolvedDescription: resolveDescription(tpl),
    });
    return true;
  }, [shouldTriggerEvent, pickTemplate, resolveDescription]);

  // Resolve choice
  const resolveEvent = useCallback(async (choiceIndex: number) => {
    if (!activeEvent || !user) return null;
    const { template, lessonId } = activeEvent;
    const choice = template.event_choices[choiceIndex];
    if (!choice) return null;

    // Apply account impact
    try {
      if (choice.amount > 0) {
        await supabase.rpc("apply_event_impact", {
          p_account_type: choice.account_type,
          p_amount: choice.amount,
          p_description: `${template.event_category}: ${template.event_name} — ${choice.label}`,
          p_is_deduction: choice.is_deduction,
        });
      }
    } catch (err) {
      console.warn("Event impact error:", err);
    }

    // Update outcomes
    const isGood = choice.quality === "excellent" || choice.quality === "good";
    try {
      const { data } = await supabase.rpc("update_learner_outcomes", {
        p_stress_delta: choice.stress_delta,
        p_resilience_delta: choice.resilience_delta,
        p_confidence_delta: choice.confidence_delta,
        p_handled_well: isGood,
      });
      if (data) setOutcomes(data as AnyValue);
    } catch (err) {
      console.warn("Outcomes update error:", err);
    }

    // Log
    await supabase.from("user_event_log").insert({
      user_id: user.id,
      event_type: "life_event",
      event_key: template.event_key,
      choice_index: choiceIndex,
      account_impact: {
        account_type: choice.account_type,
        amount: choice.amount,
        is_deduction: choice.is_deduction,
      },
      lesson_id: lessonId,
      lessons_since_last_event: lessonsSinceEvent.current,
      event_category: template.event_category,
      event_difficulty: template.event_difficulty,
      stress_delta: choice.stress_delta,
      resilience_delta: choice.resilience_delta,
      confidence_delta: choice.confidence_delta,
      outcome_quality: choice.quality,
    });

    return choice;
  }, [activeEvent, user]);

  const dismissEvent = useCallback(() => {
    setActiveEvent(null);
  }, []);

  return {
    activeEvent,
    outcomes,
    templates,
    loading,
    checkAndTriggerEvent,
    resolveEvent,
    dismissEvent,
    refetch: fetchData,
  };
}
